import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { AlertTriangle, Brain, Building2, ChevronRight, Puzzle, Wrench, type LucideIcon } from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { MarkdownViewToggle } from "@/shared/components/markdown/InlineMarkdownField";
import { markdownComponents } from "@/shared/components/markdown/markdownComponents";
import type { MarkdownView } from "@/shared/components/markdown/markdownFieldView";
import { Textarea } from "@/shared/components/ui/textarea";

import type { JobReferences } from "../types/job";
import { caretCoordinates } from "../utils/caret";
import { rehypeMentionsPlugin } from "../utils/rehypeMentions";
import {
  MENTION_KINDS,
  activeMention,
  insertKind,
  insertMention,
  matchesQuery,
  mentionId,
  mentionToken,
  mentionsOf,
  pickerStep,
  type Mention,
  type MentionKind,
} from "../utils/mentions";

interface MentionTextareaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  /** Ausente enquanto carrega: a lista abre vazia e as referências ficam sem conferência. */
  references?: JobReferences;
}

interface Option extends Mention {
  label: string;
  hint?: string;
}

/** Uma linha da lista: um tipo (primeiro passo) ou um item. */
type Row = { type: "kind"; kind: MentionKind; count: number } | { type: "item"; option: Option };

const MAX_OPTIONS = 8;
const POPUP_WIDTH = 288;

const KIND_ICON: Record<MentionKind, LucideIcon> = {
  skill: Puzzle,
  mind: Brain,
  workspace: Building2,
  tool: Wrench,
};

/**
 * A instrução de um job com referências (AB#86605, task 86871; AB#87442): digitar
 * `@` abre, onde se está digitando, a escolha do tipo — Skill, Mind, Workspace
 * ou Tool —, e escolher um tipo mostra os itens dele; escrever direto depois
 * do `@` busca em todos. Abaixo, as referências que o texto cita — com aviso
 * para a que não existe, que o api recusaria ao salvar.
 *
 * A instrução é markdown, como a Soul: `<>` mostra o código, onde se edita, e o
 * olho a renderizada, com as referências como chips. Abre renderizada quando já
 * tem texto e no código quando está vazia.
 */
export function MentionTextarea({ id, value, onChange, placeholder, rows = 4, references }: MentionTextareaProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);
  // Até a pessoa escolher ou digitar, a visão segue o conteúdo: o texto do job
  // (ou a proposta da IA) chega depois que o formulário monta.
  const [chosenView, setView] = useState<MarkdownView | null>(null);
  const view: MarkdownView = chosenView ?? (value.trim() ? "rendered" : "code");
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const allOptions = useMemo<Option[]>(
    () => [
      ...(references?.skills ?? []).map((s) => ({
        kind: "skill" as const,
        key: s.name,
        label: s.title || s.name,
        hint: s.title ? s.name : undefined,
      })),
      ...(references?.mind ?? []).map((f) => ({
        kind: "mind" as const,
        key: f.field_name,
        label: f.title || f.field_name,
        hint: f.field_name,
      })),
      ...(references?.workspace ?? []).map((field) => ({
        kind: "workspace" as const,
        key: field,
        label: t(`jobs.mentions.workspaceFields.${field}`, {
          defaultValue: field,
        }),
        hint: field,
      })),
      ...(references?.tools ?? []).map((tool) => ({
        kind: "tool" as const,
        key: tool.title,
        label: tool.title,
        hint: tool.provider,
      })),
    ],
    [references, t],
  );

  const active = activeMention(value, caret);
  const rowsShown = ((): Row[] => {
    if (!active) return [];
    const step = pickerStep(active.query);
    if (step.step === "kinds") {
      return MENTION_KINDS.map((kind) => ({
        type: "kind" as const,
        kind,
        count: allOptions.filter((o) => o.kind === kind).length,
      })).filter((row) => row.count > 0 || !references);
    }
    return allOptions
      .filter((o) => (step.kind ? o.kind === step.kind : true) && matchesQuery(active.query, o.kind, o.key, o.label))
      .slice(0, MAX_OPTIONS)
      .map((option) => ({ type: "item" as const, option }));
  })();
  // Um `@` qualquer do texto ("@time") não abre lista vazia: com as referências
  // carregadas e nada correspondendo, a lista fica fechada.
  const open =
    view === "code" && active !== null && dismissed !== active.start && (rowsShown.length > 0 || !references);

  // A lista abre logo abaixo da linha do cursor, sem passar da borda do campo.
  const anchor = active?.start ?? null;
  useLayoutEffect(() => {
    const element = ref.current;
    if (!open || anchor === null || !element) {
      setPosition(null);
      return;
    }
    const coords = caretCoordinates(element, anchor);
    const left = Math.max(0, Math.min(coords.left, element.clientWidth - POPUP_WIDTH));
    setPosition({
      top: element.offsetTop + coords.top + coords.height + 4,
      left: element.offsetLeft + left,
    });
  }, [open, anchor, value]);

  const focusAt = (next: { text: string; caret: number }) => {
    onChange(next.text);
    setCaret(next.caret);
    setHighlight(0);
    setDismissed(null);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  const choose = (row: Row) => {
    if (!active) return;
    focusAt(
      row.type === "kind"
        ? insertKind(value, active.start, caret, row.kind)
        : insertMention(value, active.start, caret, row.option),
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open || rowsShown.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlight((h) => (h + step + rowsShown.length) % rowsShown.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      choose(rowsShown[Math.min(highlight, rowsShown.length - 1)]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setDismissed(active!.start);
    }
  };

  const known = useMemo(() => new Map(allOptions.map((o) => [mentionId(o), o])), [allOptions]);
  const cited = mentionsOf(value);

  const chip = (mention: Mention, inline = false): ReactNode => {
    const option = known.get(mentionId(mention));
    const missing = Boolean(references) && !option;
    const Icon = missing ? AlertTriangle : KIND_ICON[mention.kind];
    return (
      <span
        key={mentionId(mention)}
        title={missing ? t("jobs.mentions.unknown") : mentionToken(mention)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
          inline && "mx-0.5 align-middle not-prose",
          missing ? "border-destructive/50 text-destructive" : "border-border text-muted-foreground",
        )}
      >
        <Icon className="h-3 w-3" />
        {option?.label ?? mention.key}
      </span>
    );
  };

  // A referência que o plugin marcou vira chip; os outros spans seguem como são.
  const renderedComponents: Components = {
    ...markdownComponents,
    span: ({ node, ...props }) => {
      // As marcas vêm do nó hast que o rehypeMentions criou.
      const kind = MENTION_KINDS.find((k) => k === node?.properties?.dataKind);
      const key = node?.properties?.dataKey;
      return kind && typeof key === "string" ? chip({ kind, key }, true) : <span {...props} />;
    },
  };

  const toEditor = () => {
    setView("code");
    requestAnimationFrame(() => ref.current?.focus());
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-md border border-input bg-background">
        <div className="flex items-center justify-end border-b bg-muted/30 px-2 py-1">
          <MarkdownViewToggle view={view} onChange={(next) => (next === "code" ? toEditor() : setView(next))} />
        </div>
        {view === "rendered" ? (
          // Clicar no texto também leva ao código, como num campo comum.
          <div
            role="textbox"
            tabIndex={0}
            aria-readonly
            onClick={toEditor}
            onKeyDown={(e) => {
              // Enter e Espaço ativam, como num botão; num link dentro do texto, o Enter é do link.
              if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
              e.preventDefault();
              toEditor();
            }}
            style={{ minHeight: `${rows * 1.5 + 1}rem` }}
            className="prose prose-sm max-w-none cursor-text px-3 py-2 text-sm dark:prose-invert"
          >
            {value.trim() ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeMentionsPlugin]}
                components={renderedComponents}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <p className="text-muted-foreground">{placeholder}</p>
            )}
          </div>
        ) : (
          <Textarea
            ref={ref}
            id={id}
            rows={rows}
            value={value}
            placeholder={placeholder}
            className="rounded-t-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            onFocus={() => setView("code")}
            onChange={(e) => {
              onChange(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
              setHighlight(0);
              setDismissed(null);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            onKeyDown={onKeyDown}
            onBlur={() => setTimeout(() => setDismissed(active?.start ?? null), 150)}
            aria-autocomplete="list"
            aria-expanded={open && rowsShown.length > 0}
          />
        )}
        {open && position && (
          <div
            role="listbox"
            style={{
              top: position.top,
              left: position.left,
              width: POPUP_WIDTH,
            }}
            className="absolute z-50 max-h-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
          >
            {rowsShown.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                {references ? t("jobs.mentions.empty") : t("jobs.mentions.loading")}
              </p>
            ) : (
              rowsShown.map((row, index) => {
                const kind = row.type === "kind" ? row.kind : row.option.kind;
                const Icon = KIND_ICON[kind];
                return (
                  <button
                    key={row.type === "kind" ? `kind:${kind}` : mentionId(row.option)}
                    type="button"
                    role="option"
                    aria-selected={index === highlight}
                    // mousedown, não click: o click chega depois do blur, e o blur fecha a lista.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(row);
                    }}
                    onMouseEnter={() => setHighlight(index)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                      index === highlight ? "bg-accent text-accent-foreground" : "hover:bg-accent",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    {row.type === "kind" ? (
                      <>
                        <span className="truncate">{t(`jobs.mentions.${kind}`)}</span>
                        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                          {t(`jobs.mentions.kindHint.${kind}`)}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </>
                    ) : (
                      <>
                        <span className="truncate">{row.option.label}</span>
                        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                          {t(`jobs.mentions.${kind}`)}
                        </span>
                      </>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Na renderizada os chips já estão no texto. */}
      {view === "code" && cited.length > 0 && (
        <div className="flex flex-wrap gap-1.5">{cited.map((mention) => chip(mention))}</div>
      )}
    </div>
  );
}
